import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { getEmotions, EmotionRecord } from '../services/emotionService';
import '../styles/EmotionChart.scss';
import dateIcon from '../assets/icon_date.svg';
import { getColor } from '../utils/colorUtils';

type Period = 'weekly' | 'monthly' | 'yearly';

interface ChartData {
  name: string;
  value: number;
  fill: string;
}

const formatDate = (date: Date, formatStr: string): string => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (formatStr === 'yyyy-MM-dd') {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  if (formatStr === 'M/d') {
    return `${month}/${day}`;
  }
  if (formatStr === 'yyyy-MM') {
    return `${year}-${String(month).padStart(2, '0')}`;
  }
  return date.toDateString();
};

const EmotionChart: React.FC = () => {
  const [allEmotions, setAllEmotions] = useState<EmotionRecord[]>([]);
  const [period, setPeriod] = useState<Period>('weekly');
  const [barWidth, setBarWidth] = useState(30);

  useEffect(() => {
    const fetchAllEmotions = async () => {
      try {
        const emotions = await getEmotions();
        setAllEmotions(emotions);
      } catch (error) {
        console.error('감정 데이터를 가져오는데 실패했습니다:', error);
      }
    };
    fetchAllEmotions();

    const handleResize = () => {
      if (window.innerWidth < 768) {
        setBarWidth(15);
      } else {
        setBarWidth(30);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { chartData, averageTemp, dateRange } = useMemo(() => {
    const now = new Date();
    let data: ChartData[] = [];
    let totalTemp = 0;
    let recordedDays = 0;
    let startDate = new Date();
    let endDate = new Date();

    if (period === 'weekly') {
      endDate = new Date(now);
      startDate.setDate(now.getDate() - 6);
      const weeklyEmotions = allEmotions.filter(e => {
        const eDate = new Date(e.createdAt);
        return eDate >= startDate && eDate <= endDate;
      });

      const emotionsByDate = new Map<string, number>();
      weeklyEmotions.forEach(e => {
        emotionsByDate.set(formatDate(new Date(e.createdAt), 'yyyy-MM-dd'), e.value);
        totalTemp += e.value;
        recordedDays++;
      });

      for (let i = 0; i < 7; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateString = formatDate(date, 'yyyy-MM-dd');
        const value = emotionsByDate.get(dateString) || 0;
        data.push({ name: formatDate(date, 'M/d'), value, fill: getColor(value).solid });
      }
    } else if (period === 'monthly') {
      endDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
      const monthlyEmotions = allEmotions.filter(e => {
        const eDate = new Date(e.createdAt);
        return eDate >= startDate && eDate <= endDate;
      });

      const emotionsByDate = new Map<string, number>();
      monthlyEmotions.forEach(e => {
        emotionsByDate.set(formatDate(new Date(e.createdAt), 'yyyy-MM-dd'), e.value);
        totalTemp += e.value;
        recordedDays++;
      });

      for (let i = 0; i < 30; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const dateString = formatDate(date, 'yyyy-MM-dd');
        const value = emotionsByDate.get(dateString) || 0;
        data.push({ name: formatDate(date, 'M/d'), value, fill: getColor(value).solid });
      }
    } else if (period === 'yearly') {
      endDate = new Date(now);
      startDate.setFullYear(now.getFullYear() - 1);
      const yearlyEmotions = allEmotions.filter(e => {
        const eDate = new Date(e.createdAt);
        return eDate >= startDate && eDate <= endDate;
      });

      const monthlyAverages: { [key: string]: { total: number; count: number } } = {};
      yearlyEmotions.forEach(e => {
        const monthKey = formatDate(new Date(e.createdAt), 'yyyy-MM');
        if (!monthlyAverages[monthKey]) {
          monthlyAverages[monthKey] = { total: 0, count: 0 };
        }
        monthlyAverages[monthKey].total += e.value;
        monthlyAverages[monthKey].count++;
      });

      for (let i = 11; i >= 0; i--) {
        const date = new Date(now);
        date.setMonth(now.getMonth() - i);
        const monthKey = formatDate(date, 'yyyy-MM');
        const monthData = monthlyAverages[monthKey];
        const value = monthData ? Math.round(monthData.total / monthData.count) : 0;
        if (value !== 0) {
          totalTemp += value;
          recordedDays++;
        }
        data.push({ name: `${date.getMonth() + 1}월`, value, fill: getColor(value).solid });
      }
    }

    return {
      chartData: data,
      averageTemp: recordedDays > 0 ? Math.round(totalTemp / recordedDays) : 0,
      dateRange: `${formatDate(startDate, 'yyyy-MM-dd')} ~ ${formatDate(endDate, 'yyyy-MM-dd')}`,
    };
  }, [allEmotions, period]);

  return (
    <div className="emotion-chart-widget">
      <div className="widget-header">
        <div className="period-toggle">
          <button
            onClick={() => setPeriod('weekly')}
            className={period === 'weekly' ? 'active' : ''}
          >
            주간
          </button>
          <button
            onClick={() => setPeriod('monthly')}
            className={period === 'monthly' ? 'active' : ''}
          >
            월간
          </button>
          <button
            onClick={() => setPeriod('yearly')}
            className={period === 'yearly' ? 'active' : ''}
          >
            연간
          </button>
        </div>
        <div className="date-range-display">
          <img src={dateIcon} alt="date icon" />
          <span>{dateRange}</span>
        </div>
      </div>

      <div className="chart-title-section">
        <h3>마음 온도 변화</h3>
        <p>
          선택한 기간의 평균 마음 온도는 <span>{averageTemp}°</span> 입니다.
        </p>
      </div>

      <div className="chart-container">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 20, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" tickLine={false} axisLine={false} dy={10} />
            <YAxis domain={[0, 38]} tickCount={8} tickLine={false} axisLine={false} />
            <Tooltip
              cursor={{ fill: 'transparent' }}
              contentStyle={{
                borderRadius: '10px',
                borderColor: '#EAEAEA',
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
              }}
            />
            <Legend />
            <Bar dataKey="value" name="마음 온도" radius={[4, 4, 0, 0]} barSize={barWidth}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--primary-color)"
              strokeWidth={2}
              name="추세"
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default EmotionChart;
